/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { StatusPill } from '../../../shared/surface-cards/StatusPill';
import { ticketColors } from '../../../shared/theme/ticketColors';
import type { BrandRecord } from '../../domain/catalogBrandTypes'
import type { SupplierRecord } from '../../domain/catalogSupplierTypes';
import { supplierPaymentTermsLabel } from '../../domain/catalogSupplierTypes';
import { formatMoney } from '../../../shared/format/public-api';
import type { CategoryAppliesTo, CategoryRecord } from '../../domain/categoryTypes';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import { modalShellShared } from '../../../shared/modals/modalShellShared.styles';
import { TicketModalHeader } from '../../../shared/ticket/TicketModalHeader'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { formatLocalDate } from '@shared/vueltoDateTime';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { usePublicStoreFeaturesVisible } from '../../../shell/feature-flags/usePublicStoreFeaturesVisible';

import { mastersModalStyles as styles } from './mastersModal.styles';
import { formatArPhoneDisplay } from '@shared/phone';
import { spacing } from '../../../shared/theme/spacing';

type EntityType = 'brand' | 'supplier' | 'category';

type BaseProps = {
  visible: boolean;
  canEdit: boolean;
  onClose: () => void;
  /** Omit for canonical read-only entities (e.g. categories). */
  onEdit?: () => void;
  onToggleActive: () => void;
};

type BrandProps = BaseProps & { entityType: 'brand'; entity: BrandRecord };
type SupplierProps = BaseProps & { entityType: 'supplier'; entity: SupplierRecord };
type CategoryProps = BaseProps & { entityType: 'category'; entity: CategoryRecord };

export type MasterDetailModalProps = BrandProps | SupplierProps | CategoryProps;

type DetailRow = {
  label: string;
  value: string | null | undefined;
  selectable?: boolean;
};

const WIDE_MODAL_BREAKPOINT = 768;

const ENTITY_COPY: Record<
  EntityType,
  { kicker: string; headerTitle: string; activeLabel: string; inactiveLabel: string }
> = {
  brand: {
    kicker: 'Marca',
    headerTitle: 'Detalle de marca',
    activeLabel: 'Activa',
    inactiveLabel: 'Inactiva',
  },
  category: {
    kicker: 'Categoría',
    headerTitle: 'Detalle de categoría',
    activeLabel: 'Activa en tu negocio',
    inactiveLabel: 'Oculta en tu negocio',
  },
  supplier: {
    kicker: 'Proveedor',
    headerTitle: 'Detalle de proveedor',
    activeLabel: 'Activo',
    inactiveLabel: 'Inactivo',
  },
};

function DetailInfoPanel({ title, rows }: { title: string; rows: DetailRow[] }) {
  const visibleRows = rows.filter((row) => row.value != null && String(row.value).trim() !== '');
  if (visibleRows.length === 0) return null;

  return (
    <View style={modalExperience.infoPanel}>
      <Text style={modalExperience.detailBlockTitle}>{title}</Text>
      {visibleRows.map((row) => (
        <View key={`${title}-${row.label}`} style={modalExperience.detailKvRow}>
          <Text style={modalExperience.detailMetaLabel}>{row.label}</Text>
          <Text
            selectable={row.selectable}
            style={modalExperience.detailKvValueStrong}
            numberOfLines={3}
          >
            {row.value}
          </Text>
        </View>
      ))}
    </View>
  );
}

function appliesToLabel(appliesTo: CategoryAppliesTo): string {
  if (appliesTo === 'service') return 'Servicios';
  if (appliesTo === 'both') return 'Ambos';
  return 'Productos';
}

function formatWebsiteHost(website: string | null | undefined): string | null {
  const raw = website?.trim();
  if (!raw) return null;
  try {
    return new URL(raw).hostname.replace(/^www\./i, '');
  } catch {
    return raw;
  }
}

function buildDetailContent(props: MasterDetailModalProps, publicStoreFeaturesEnabled: boolean) {
  const { entityType, entity } = props;
  const copy = ENTITY_COPY[entityType];
  const isActive = entity.isActive;
  const statusLabel = isActive ? copy.activeLabel : copy.inactiveLabel;

  let heroMetric: { value: string; label: string } | null = null;
  let heroSubtitle: string | null = null;
  const panels: Array<{ title: string; rows: DetailRow[] }> = [];

  if (entityType === 'brand') {
    heroMetric =
      entity.defaultMarkupPercent != null
        ? { value: `${entity.defaultMarkupPercent}%`, label: 'Margen' }
        : null;
    heroSubtitle = formatWebsiteHost(entity.website);
    panels.push({
      title: 'Identificación',
      rows: [
        { label: 'Código', value: entity.code },
        { label: 'Sitio web', value: entity.website, selectable: true },
      ],
    });
  } else if (entityType === 'category') {
    heroMetric = { value: appliesToLabel(entity.appliesTo), label: 'Aplica a' };
    heroSubtitle = entity.parentId
      ? 'Subcategoría del rubro. Solo la activás o la ocultás.'
      : 'Categoría del rubro. Solo la activás o la ocultás.';
    panels.push({
      title: 'Del rubro',
      rows: [
        { label: 'Aplica a', value: appliesToLabel(entity.appliesTo) },
        ...(publicStoreFeaturesEnabled
          ? [{ label: 'Verificación 18+', value: entity.isRestricted ? 'Sí' : 'No' }]
          : []),
        { label: 'ID', value: entity.id, selectable: true },
        { label: 'Slug', value: entity.slug, selectable: true },
        { label: 'Descripción', value: entity.description },
      ],
    });
  } else {
    const location = [entity.city?.trim(), entity.state?.trim()].filter(Boolean).join(', ');
    heroMetric =
      entity.leadTimeDays != null
        ? { value: `${entity.leadTimeDays}`, label: 'Días de entrega' }
        : location
          ? { value: location, label: 'Ubicación' }
          : null;
    heroSubtitle = entity.contactName?.trim() || entity.contactEmail?.trim() || null;

    panels.push({
      title: 'Identificación',
      rows: [
        { label: 'CUIT', value: entity.taxId, selectable: true },
        { label: 'Código interno', value: entity.code },
        { label: 'Cuenta cliente', value: entity.accountNumber },
      ],
    });
    panels.push({
      title: 'Contacto',
      rows: [
        { label: 'Nombre', value: entity.contactName },
        { label: 'Email', value: entity.contactEmail, selectable: true },
        {
          label: 'Teléfono',
          value: entity.contactPhone ? formatArPhoneDisplay(entity.contactPhone) : entity.contactPhone,
          selectable: true,
        },
        { label: 'Sitio web', value: entity.website, selectable: true },
      ],
    });
    panels.push({
      title: 'Dirección',
      rows: [
        { label: 'Calle', value: entity.address },
        { label: 'Ciudad', value: entity.city },
        { label: 'Provincia', value: entity.state },
        { label: 'Código postal', value: entity.zip },
      ],
    });
    panels.push({
      title: 'Pago y entrega',
      rows: [
        { label: 'Plazo de pago', value: supplierPaymentTermsLabel(entity.paymentTerms) },
        {
          label: 'Días de entrega',
          value: entity.leadTimeDays != null ? `${entity.leadTimeDays} días` : null,
        },
        {
          label: 'Monto mínimo',
          value: entity.minimumOrderAmount != null ? formatMoney(entity.minimumOrderAmount) : null,
        },
      ],
    });
    if (entity.notes?.trim()) {
      panels.push({
        title: 'Notas internas',
        rows: [{ label: 'Notas', value: entity.notes }],
      });
    }
  }

  panels.push({
    title: 'Registro',
    rows: [
      { label: 'Creado', value: entity.createdAt ? formatLocalDate(entity.createdAt) : null },
      { label: 'Actualizado', value: entity.updatedAt ? formatLocalDate(entity.updatedAt) : null },
    ],
  });

  return {
    copy,
    title: entity.name,
    heroSubtitle,
    heroMetric,
    statusLabel,
    isActive,
    panels,
  };
}

export function MasterDetailModal(props: MasterDetailModalProps) {
  const { visible, canEdit, onClose, onEdit, onToggleActive } = props;
  const { width, height: winH } = useWindowDimensions();
  const isWide = width >= WIDE_MODAL_BREAKPOINT;
  const isMobile = !isWide;
  const insets = useSafeAreaInsets();
  const modalBackdropPadding = useModalBackdropPadding();
  const publicStoreFeaturesEnabled = usePublicStoreFeaturesVisible();

  const content = useMemo(
    () => (visible ? buildDetailContent(props, publicStoreFeaturesEnabled) : null),
    [visible, props, publicStoreFeaturesEnabled]
  );

  if (!visible || !content) return null;

  const actionsGrid = (
    <View style={modalExperience.detailActionsGrid}>
      {canEdit && onEdit ? (
        <TouchableOpacity
          style={[modalExperience.detailActionBtn, modalExperience.detailActionBtnPrimary]}
          onPress={onEdit}
          accessibilityRole="button"
          accessibilityLabel="Editar"
        >
          <Ionicons name="create-outline" size={16} color={ticketColors.goldInk} />
          <Text style={[modalExperience.detailActionText, modalExperience.detailActionTextPrimary]}>
            Editar
          </Text>
        </TouchableOpacity>
      ) : null}
      {canEdit ? (
        <TouchableOpacity
          style={[
            modalExperience.detailActionBtn,
            onEdit ? null : modalExperience.detailActionBtnPrimary,
            !content.isActive && modalExperience.detailActionBtnDanger,
          ]}
          onPress={onToggleActive}
          accessibilityRole="button"
          accessibilityLabel={content.isActive ? 'Desactivar' : 'Activar'}
        >
          <Ionicons
            name={content.isActive ? 'eye-off-outline' : 'eye-outline'}
            size={16}
            color={
              !content.isActive
                ? ticketColors.bad
                : onEdit
                  ? ticketColors.ink
                  : ticketColors.goldInk
            }
          />
          <Text
            style={[
              modalExperience.detailActionText,
              !onEdit && content.isActive ? modalExperience.detailActionTextPrimary : null,
              !content.isActive && modalExperience.detailActionTextDanger,
            ]}
          >
            {content.isActive ? 'Desactivar' : 'Activar'}
          </Text>
        </TouchableOpacity>
      ) : null}
      <TouchableOpacity
        style={modalExperience.detailActionBtn}
        onPress={onClose}
        accessibilityRole="button"
        accessibilityLabel="Cerrar"
      >
        <Text style={modalExperience.detailActionText}>Cerrar</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={onClose}>
      <Pressable
        style={[styles.modalBackdrop, isMobile ? { justifyContent: 'flex-end' } : modalExperience.modalRoot, isMobile ? null : modalBackdropPadding]}
        onPress={onClose}
      >
        <Pressable
          style={[
            isMobile
              ? [styles.sheetCard, { maxHeight: winH * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) }]
              : [
                  styles.modalCard,
                  modalShellShared.modalCardShell,
                  isWide ? modalExperience.modalWidthComfortable : modalExperience.modalWidthStandard,
                ],
          ]}
          onPress={(event) => event.stopPropagation()}
        >
          {isMobile ? <View style={styles.grabber} /> : null}
          <TicketModalHeader
            kicker="Catálogo"
            title={content.copy.headerTitle}
            onClose={onClose}
            compact={isMobile}
          />
          <ScrollView
            style={styles.modalScroll}
            contentContainerStyle={styles.modalScrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
          >
            <View style={modalExperience.heroBand}>
              <View style={modalExperience.heroText}>
                <Text style={modalExperience.heroKicker}>{content.copy.kicker}</Text>
                <Text style={modalExperience.heroTitle} numberOfLines={2}>
                  {content.title}
                </Text>
                {content.heroSubtitle ? (
                  <Text style={modalExperience.heroSubtitle} numberOfLines={2}>
                    {content.heroSubtitle}
                  </Text>
                ) : null}
                <View style={styles.heroStatusRow}>
                  <StatusPill
                    label={content.statusLabel}
                    tone={content.isActive ? 'success' : 'danger'}
                    compact
                  />
                </View>
              </View>
              {content.heroMetric ? (
                <View style={modalExperience.countBadge}>
                  <Text style={modalExperience.countValue} numberOfLines={1}>
                    {content.heroMetric.value}
                  </Text>
                  <Text style={modalExperience.countLabel}>{content.heroMetric.label}</Text>
                </View>
              ) : null}
            </View>

            {content.panels.map((panel) => (
              <DetailInfoPanel key={panel.title} title={panel.title} rows={panel.rows} />
            ))}
          </ScrollView>

          <View style={styles.modalActionsDock}>{actionsGrid}</View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
